import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MapPin, Calendar } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    contactMessage: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleCallNow = () => {
    window.location.href = 'tel:2487853734';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(
        'https://ggavtngjpeppxzdahgwx.supabase.co/functions/v1/92ccd039-a02f-4c95-a8b1-8e5d78f01f8f',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...contactForm,
            formType: 'contact_message'
          })
        }
      );

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast({
          title: "Success!",
          description: "Your message has been sent. We'll contact you soon!",
        });
        
        setContactForm({
          contactFirstName: '',
          contactLastName: '',
          contactEmail: '',
          contactPhone: '',
          contactMessage: ''
        });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to send your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-slate-600 to-slate-800 bg-clip-text text-transparent">
            Ready to Get Started?
          </h2>
          <Button 
            onClick={handleCallNow}
            size="lg"
            className="mt-6 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white font-bold px-8 py-3 text-lg shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            📞 Call Now: (248) 785-3734
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          <Card className="shadow-xl border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-slate-900">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <Phone className="w-6 h-6 text-slate-600" />
                <div>
                  <p className="font-semibold text-slate-900">Phone</p>
                  <p className="text-slate-700">(248) 785-3734</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Mail className="w-6 h-6 text-slate-600" />
                <div>
                  <p className="font-semibold text-slate-900">Email</p>
                  <p className="text-slate-700">info@bridgeriverllc.com</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <MapPin className="w-6 h-6 text-slate-600" />
                <div>
                  <p className="font-semibold text-slate-900">Office</p>
                  <p className="text-slate-700">40900 Woodward Ave, St 150<br />Bloomfield Hills, MI 48304</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Calendar className="w-6 h-6 text-slate-600" />
                <div>
                  <p className="font-semibold text-slate-900">Hours</p>
                  <p className="text-slate-700">Mon-Fri: 9AM-6PM</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-xl border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-slate-900">Send a Message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input 
                    name="contactFirstName"
                    placeholder="First Name" 
                    className="border-slate-200" 
                    value={contactForm.contactFirstName}
                    onChange={handleInputChange}
                    required
                  />
                  <Input 
                    name="contactLastName"
                    placeholder="Last Name" 
                    className="border-slate-200" 
                    value={contactForm.contactLastName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <Input 
                  name="contactEmail"
                  placeholder="Email Address" 
                  type="email" 
                  className="border-slate-200" 
                  value={contactForm.contactEmail}
                  onChange={handleInputChange}
                  required
                />
                <Input 
                  name="contactPhone"
                  placeholder="Phone Number" 
                  type="tel" 
                  className="border-slate-200" 
                  value={contactForm.contactPhone}
                  onChange={handleInputChange}
                  required
                />
                <Textarea 
                  name="contactMessage"
                  placeholder="Tell me about your financial goals..." 
                  rows={4} 
                  className="border-slate-200" 
                  value={contactForm.contactMessage}
                  onChange={handleInputChange}
                  required
                />
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-bold py-3"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="shadow-xl border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-slate-900">Office Location</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-center">
                  <p className="font-semibold text-slate-900">Bridgeriver Advisors</p>
                  <p className="text-slate-700">40900 Woodward Ave, Suite 150</p>
                  <p className="text-slate-700">Bloomfield Hills, MI 48304</p>
                </div>
                <div className="w-full h-64 bg-slate-100 rounded-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2943.8!2d-83.2467!3d42.5394!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8824b2a0c0a0a0a0%3A0x0!2s40900%20Woodward%20Ave%2C%20Bloomfield%20Hills%2C%20MI%2048304!5e0!3m2!1sen!2sus!4v1234567890123"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Office Location"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;